import { useUser } from "@clerk/nextjs";
import { updateClerkPublicMetaDataAPI } from "helpers/api";
import { convertUserPublicMetadata, IClerkPublicMetaData } from "helpers/clerk";
import { useCallback, useMemo } from "react";

/**
 * @component useClerkMetadata
 * @description Loads clerk metadata from the useUser hook and converts it into the correct type. Also exposes a method to update the metadata which handles both local state and API updates.
 */
function useClerkMetadata() {
  const { user } = useUser();

  // Convert the public metadata to the correct type.
  const userMetadata = useMemo(() => {
    if (!user?.publicMetadata) return undefined;
    return convertUserPublicMetadata(user.publicMetadata);
  }, [user?.publicMetadata]);

  // Update the metadata in the Clerk API. This will automatically be updated on the user object in the clerk hook, so we don't need to manage any local state.
  const updateUserMetadata = useCallback(
    async (newMetadata: Partial<IClerkPublicMetaData>) => {
      if (userMetadata === undefined || userMetadata === null) return;
      await updateClerkPublicMetaDataAPI({ ...userMetadata, ...newMetadata });
      await user?.reload();
    },
    [userMetadata, user]
  );

  return {
    isUserMetadataLoaded: userMetadata !== undefined && userMetadata !== null,
    userMetadata,
    updateUserMetadata,
  };
}

export default useClerkMetadata;
